import { TSystemResponseDto } from '@app-velocity/av-api-v2';

import { t } from '@app-velocity/i18n';

import { formatSystemResponseDelay } from './formatSystemResponseDelay';
import { parseSystemResponseDetails } from './parseSystemResponseDetails';

export const getSystemResponseDescription = (
  response: TSystemResponseDto
): string | null => {
  const { type, action, elementName, details } = response;

  if (type === 'NO_SUITABLE_ACTION_FOUND') {
    return t('prototypes:system_response.no_action');
  }

  if (type === 'NO_AVAILABLE_ACTION') {
    return t('prototypes:system_response.no_available_action');
  }

  if (type === 'WAITING_PERIOD_TOO_LONG') {
    const parsedDetails = parseSystemResponseDetails(details);

    if ('time' in parsedDetails) {
      return t('prototypes:system_response.waited_long_enough', {
        time: parsedDetails.time,
      });
    }

    return t('prototypes:system_response.waited_long_enough_skipped');
  }

  if (type === 'DELAYED_ACTION_FOUND') {
    const parsedDetails = parseSystemResponseDetails(details);
    const formattedDelay = formatSystemResponseDelay(
      parsedDetails.delay as number | string | undefined
    );

    switch (action) {
      case 'navigate':
        return t('prototypes:system_response.navigated_after_delay', {
          delay: formattedDelay,
        });
      case 'change':
        return t('prototypes:system_response.changed_variant_after_delay', {
          delay: formattedDelay,
        });
      case 'overlay':
      case 'swap-overlay':
        return t('prototypes:system_response.opened_overlay_after_delay', {
          delay: formattedDelay,
        });
      case 'close-overlay':
        return t('prototypes:system_response.closed_overlay_after_delay', {
          delay: formattedDelay,
        });
    }

    return null;
  }

  switch (action) {
    case 'navigate':
      return t('prototypes:system_response.navigated');
    case 'navigate_selected_different':
      return t(
        'prototypes:system_response.used_different_action_and_navigated',
        { selectedInstead: elementName }
      );
    case 'navigate_selected_default':
      return t('prototypes:system_response.used_default_action_and_navigated', {
        action: 'delay',
      });
    case 'change':
      return t('prototypes:system_response.changed_variant');
    case 'change_selected_different':
      return t(
        'prototypes:system_response.used_different_action_and_changed_variant',
        { selectedInstead: elementName }
      );
    case 'change_selected_default':
      return t(
        'prototypes:system_response.used_default_action_and_changed_variant',
        { action: 'delay' }
      );
    case 'overlay':
    case 'swap-overlay':
      return t('prototypes:system_response.opened_overlay');
    case 'close-overlay':
      return t('prototypes:system_response.closed_overlay');
    case 'overlay_selected_different':
      return t(
        'prototypes:system_response.used_different_action_and_opened_overlay',
        { selectedInstead: elementName }
      );
    case 'overlay_selected_default':
      return t(
        'prototypes:system_response.used_default_action_and_opened_overlay',
        { action: 'delay' }
      );
  }

  return null;
};
